import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Check, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  passwordPolicyChecks,
  isPasswordValid,
  passwordsMatch,
  PASSWORD_MIN_LENGTH,
} from "@/lib/passwordPolicy";
import { toast } from "sonner";

export default function Settings() {
  const { user, platformMode, changePassword } = useAuth();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Fase 1I.0 — estado local da seção "Segurança" (só em modo platform).
  const [pwdCur, setPwdCur] = useState("");
  const [pwdNew, setPwdNew] = useState("");
  const [pwdConf, setPwdConf] = useState("");
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdError, setPwdError] = useState("");
  // Olhinho independente por campo (não persiste fora do estado local).
  const [showCur, setShowCur] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConf, setShowConf] = useState(false);

  useEffect(() => {
    api.get("/company").then(({ data }) => { setCompany(data); setName(data?.name || ""); }).catch((e) => setError(formatApiError(e)));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/company", { name });
      toast.success("Empresa atualizada.");
      const { data } = await api.get("/company");
      setCompany(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="settings-page">
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Conta</div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight mt-2">Configurações</h1>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
        <h2 className="font-heading text-lg font-semibold">Empresa</h2>
        {!company ? (
          <p className="text-sm text-slate-500 mt-2">Carregando...</p>
        ) : user?.role === "admin" ? (
          <form onSubmit={save} className="mt-4 space-y-4" data-testid="company-form">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Nome da empresa</label>
              <input required value={name} onChange={(e) => setName(e.target.value)} data-testid="company-name-input"
                className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
            </div>
            <button type="submit" disabled={saving} data-testid="company-save-button"
              className="h-10 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-50">
              {saving ? "Salvando..." : "Salvar"}
            </button>
          </form>
        ) : (
          <p className="text-sm text-slate-600 mt-2">{company.name}</p>
        )}
        {error && <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
        <h2 className="font-heading text-lg font-semibold">Seu perfil</h2>
        <dl className="mt-4 text-sm grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><dt className="text-slate-500">Nome</dt><dd className="text-slate-900 font-medium">{user?.name}</dd></div>
          <div><dt className="text-slate-500">E-mail</dt><dd className="text-slate-900 font-medium">{user?.email}</dd></div>
          <div><dt className="text-slate-500">Função</dt><dd className="text-slate-900 font-medium">{user?.role}</dd></div>
          <div><dt className="text-slate-500">Status</dt><dd className="text-slate-900 font-medium">{user?.status}</dd></div>
        </dl>
      </div>

      {platformMode && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid="platform-security-section">
          <h2 className="font-heading text-lg font-semibold">Segurança</h2>
          <p className="text-sm text-slate-500 mt-1">
            Altere sua senha global. Após confirmar, você será desconectado e precisará entrar novamente.
          </p>
          <form
            className="mt-4 space-y-3 max-w-md"
            onSubmit={async (e) => {
              e.preventDefault();
              setPwdError("");
              if (!pwdCur || !pwdNew || !pwdConf) {
                setPwdError("Preencha todos os campos.");
                return;
              }
              if (pwdNew !== pwdConf) {
                setPwdError("A confirmação não coincide com a nova senha.");
                return;
              }
              if (!isPasswordValid(pwdNew)) {
                setPwdError(`A nova senha não atende aos critérios (mínimo ${PASSWORD_MIN_LENGTH} caracteres).`);
                return;
              }
              setPwdSaving(true);
              try {
                await changePassword({
                  current_password: pwdCur,
                  new_password: pwdNew,
                  confirm_password: pwdConf,
                });
                toast.success("Senha alterada. Faça login novamente.");
                setPwdCur(""); setPwdNew(""); setPwdConf("");
                navigate("/login");
              } catch (err) {
                setPwdError(formatApiError(err));
              } finally {
                setPwdSaving(false);
              }
            }}
          >
            <label className="block">
              <span className="text-xs text-slate-600">Senha atual</span>
              <div className="relative mt-1">
                <input
                  type={showCur ? "text" : "password"}
                  autoComplete="current-password"
                  value={pwdCur}
                  onChange={(e) => setPwdCur(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 pr-10"
                  data-testid="platform-security-current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowCur((v) => !v)}
                  aria-label={showCur ? "Ocultar senha atual" : "Mostrar senha atual"}
                  className="absolute inset-y-0 right-0 flex items-center px-2 text-slate-400 hover:text-slate-700"
                  data-testid="platform-security-current-toggle"
                >
                  {showCur ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
              </div>
            </label>
            <label className="block">
              <span className="text-xs text-slate-600">Nova senha</span>
              <div className="relative mt-1">
                <input
                  type={showNew ? "text" : "password"}
                  autoComplete="new-password"
                  value={pwdNew}
                  onChange={(e) => setPwdNew(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 pr-10"
                  data-testid="platform-security-new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowNew((v) => !v)}
                  aria-label={showNew ? "Ocultar nova senha" : "Mostrar nova senha"}
                  className="absolute inset-y-0 right-0 flex items-center px-2 text-slate-400 hover:text-slate-700"
                  data-testid="platform-security-new-toggle"
                >
                  {showNew ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
              </div>
            </label>
            <label className="block">
              <span className="text-xs text-slate-600">Confirmar nova senha</span>
              <div className="relative mt-1">
                <input
                  type={showConf ? "text" : "password"}
                  autoComplete="new-password"
                  value={pwdConf}
                  onChange={(e) => setPwdConf(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 pr-10"
                  data-testid="platform-security-confirm-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConf((v) => !v)}
                  aria-label={showConf ? "Ocultar confirmação" : "Mostrar confirmação"}
                  className="absolute inset-y-0 right-0 flex items-center px-2 text-slate-400 hover:text-slate-700"
                  data-testid="platform-security-confirm-toggle"
                >
                  {showConf ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </label>
            <ul className="text-xs text-slate-600 space-y-1" data-testid="platform-security-criteria">
              {passwordPolicyChecks(pwdNew).map((c) => (
                <li key={c.key} className="flex items-center gap-2">
                  {c.ok
                    ? <Check className="w-3.5 h-3.5 text-emerald-600" aria-hidden="true" />
                    : <X className="w-3.5 h-3.5 text-slate-400" aria-hidden="true" />}
                  <span className={c.ok ? "text-emerald-700" : "text-slate-600"}>{c.label}</span>
                </li>
              ))}
              <li className="flex items-center gap-2" data-testid="platform-security-match">
                {passwordsMatch(pwdNew, pwdConf)
                  ? <Check className="w-3.5 h-3.5 text-emerald-600" aria-hidden="true" />
                  : <X className="w-3.5 h-3.5 text-slate-400" aria-hidden="true" />}
                <span className={passwordsMatch(pwdNew, pwdConf) ? "text-emerald-700" : "text-slate-600"}>
                  As duas senhas precisam ser iguais
                </span>
              </li>
            </ul>
            {pwdError && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="platform-security-error">
                {pwdError}
              </div>
            )}
            <button
              type="submit"
              disabled={pwdSaving}
              className="bg-slate-900 text-white text-sm px-4 py-2 rounded-lg disabled:opacity-60"
              data-testid="platform-security-submit"
            >
              {pwdSaving ? "Alterando..." : "Alterar senha"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
